import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchClientOrders,
  fetchSalesReport,
  fetchVendors,
  type ClientOrdersReport,
  type ClientProductRow,
  type ClientSalesSummary,
  type SalesReport,
  type VendorSummary,
} from "@/lib/adminReportsApi";
import { getApiBaseUrl, getApiErrorMessage } from "@/lib/api";
import { useApp } from "@/store/useApp";
import { hasPermission, isAdminEquivalent } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Download, ExternalLink, FileSpreadsheet, FileText, Layers, Loader2, Package, TrendingUp, X } from "lucide-react";

function fmt(value: number) {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtNumber(value: number) {
  return value.toLocaleString("pt-BR", { maximumFractionDigits: 3 });
}

// O clientName vem como snapshot "Cliente — Unidade" (backend compõe com " — "). No cabeçalho do
// grupo exibimos só a parte do cliente; a unidade já aparece nas sublinhas do "Detalhar".
function baseClientName(name: string): string {
  const idx = name.indexOf(" — ");
  return idx >= 0 ? name.slice(0, idx) : name;
}

function fmtBRDate(iso: string | null | undefined) {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

type ProductSort = "mais-pedidos" | "menos-pedidos" | "maior-valor" | "maior-quantidade";

const PRODUCT_SORT_LABELS: Record<ProductSort, string> = {
  "mais-pedidos": "Mais pedidos",
  "menos-pedidos": "Menos pedidos",
  "maior-valor": "Maior valor",
  "maior-quantidade": "Maior quantidade",
};

function sortProducts(products: ClientProductRow[], sort: ProductSort): ClientProductRow[] {
  const copy = [...products];
  switch (sort) {
    case "mais-pedidos":
      return copy.sort((a, b) => b.orderCount - a.orderCount);
    case "menos-pedidos":
      return copy.sort((a, b) => a.orderCount - b.orderCount);
    case "maior-valor":
      return copy.sort((a, b) => b.totalValue - a.totalValue);
    case "maior-quantidade":
      return copy.sort((a, b) => b.totalQuantity - a.totalQuantity);
  }
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function firstDayOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

function exportCsv(report: SalesReport, vendorLabel: string, detailed: boolean) {
  const header = ["Cliente", "Pedidos", "Total", "Entregue", "A Entregar", "% Comissão", "Comissão"];
  const rows: (string | number)[][] = [];
  for (const c of report.byClient) {
    rows.push([
      baseClientName(c.clientName),
      c.orders,
      c.totalValue.toFixed(2),
      c.deliveredValue.toFixed(2),
      c.pendingValue.toFixed(2),
      c.commissionPercent != null ? c.commissionPercent.toFixed(2) : "",
      c.commissionAmount != null ? c.commissionAmount.toFixed(2) : "",
    ]);
    if (detailed) {
      for (const u of c.byDeliveryUnit ?? []) {
        rows.push([
          `    ${u.deliveryUnitName}`,
          u.orders,
          u.totalValue.toFixed(2),
          u.deliveredValue.toFixed(2),
          u.pendingValue.toFixed(2),
          "",
          "",
        ]);
      }
    }
  }
  const totalsRow = [
    vendorLabel || "Todos",
    report.totalOrders,
    report.totalValue.toFixed(2),
    report.deliveredValue.toFixed(2),
    report.pendingValue.toFixed(2),
    "",
    report.commissionTotal != null ? report.commissionTotal.toFixed(2) : "",
  ];

  const csv = [header, ...rows, [], ["TOTAL", ...totalsRow.slice(1)]]
    .map(r => r.join(";"))
    .join("\n");

  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vendas_${report.periodFrom ?? "all"}_${report.periodTo ?? "all"}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AdminSalesReport() {
  const { account } = useApp();
  const isVendedor = account?.role === "VENDEDOR";
  const canFilterVendor = isAdminEquivalent(account?.role) || account?.role === "PLANEJADOR";
  const canExport = hasPermission(account, "RELATORIOS.VENDAS.EXPORTAR");

  const [vendors, setVendors] = useState<VendorSummary[]>([]);
  const [report, setReport] = useState<SalesReport | null>(null);
  const [loading, setLoading] = useState(false);

  // Filters
  const [from, setFrom] = useState(firstDayOfMonth());
  const [to, setTo] = useState(today());
  const [vendorId, setVendorId] = useState<string>("__all__");
  const [exporting, setExporting] = useState<"pdf" | "xlsx" | null>(null);
  // Quando ativo, expande cada cliente em sublinhas por Unidade de entrega (dados já vêm no relatório).
  const [detailed, setDetailed] = useState(false);

  // Detalhe do cliente selecionado (pedidos + produtos).
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientReport, setClientReport] = useState<ClientOrdersReport | null>(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [productSort, setProductSort] = useState<ProductSort>("mais-pedidos");

  const openClient = useCallback(async (client: ClientSalesSummary) => {
    setSelectedClientId(client.clientId);
    setClientReport(null);
    setClientLoading(true);
    try {
      const data = await fetchClientOrders(client.clientId, { from: from || undefined, to: to || undefined });
      setClientReport(data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erro ao carregar os pedidos do cliente"));
      setSelectedClientId(null);
    } finally {
      setClientLoading(false);
    }
  }, [from, to]);

  const sortedProducts = useMemo(
    () => (clientReport ? sortProducts(clientReport.products, productSort) : []),
    [clientReport, productSort],
  );

  const handleExportServer = useCallback(async (format: "pdf" | "xlsx") => {
    setExporting(format);
    try {
      const qs = new URLSearchParams();
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      if (canFilterVendor && vendorId !== "__all__") qs.set("vendorId", vendorId);
      if (detailed) qs.set("detailed", "true");
      const resp = await fetch(`${getApiBaseUrl()}/api/v1/reports/sales/export.${format}?${qs.toString()}`, {
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Erro ao exportar");
      const disposition = resp.headers.get("content-disposition") ?? "";
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match?.[1] ?? `vendas.${format}`;
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao exportar");
    } finally {
      setExporting(null);
    }
  }, [from, to, vendorId, canFilterVendor, detailed]);

  // Load vendor list for ADMIN/PLANEJADOR
  useEffect(() => {
    if (canFilterVendor) {
      fetchVendors()
        .then(setVendors)
        .catch(() => toast.error("Erro ao carregar vendedores"));
    }
  }, [canFilterVendor]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    // Fecha o painel de cliente para não exibir pedidos de um período/vendedor diferente do recarregado.
    setSelectedClientId(null);
    setClientReport(null);
    try {
      const data = await fetchSalesReport({
        from: from || undefined,
        to: to || undefined,
        vendorId: canFilterVendor && vendorId !== "__all__" ? vendorId : undefined,
      });
      setReport(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar relatório");
    } finally {
      setLoading(false);
    }
  }, [from, to, vendorId, canFilterVendor]);

  // Auto-load on mount
  useEffect(() => { void loadReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const vendorLabel = report?.vendorName ?? (vendorId === "__all__" ? "Todos os vendedores" : vendors.find(v => v.id === vendorId)?.name ?? "");

  const statCards = report ? [
    { label: "Total de pedidos", value: String(report.totalOrders), sub: "não cancelados" },
    { label: "Valor total", value: fmt(report.totalValue), sub: "não cancelados" },
    { label: "Entregue", value: fmt(report.deliveredValue), sub: "status ENTREGUE" },
    { label: "A entregar", value: fmt(report.pendingValue), sub: "em aberto" },
    { label: "Comissão", value: fmt(report.commissionTotal ?? 0), sub: "sobre entregue" },
  ] : [];

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-6 text-primary-foreground">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Relatório de Vendas</h1>
            <p className="mt-1 text-sm text-primary-foreground/80">
              {isVendedor ? "Seus pedidos e clientes." : "Performance de vendas por período e vendedor."}
            </p>
          </div>
          {report && report.byClient.length > 0 && canExport && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="gap-2"
                onClick={() => exportCsv(report, vendorLabel, detailed)}
              >
                <Download className="h-4 w-4" />
                CSV
              </Button>
              <Button
                variant="secondary"
                className="gap-2"
                onClick={() => void handleExportServer("pdf")}
                disabled={exporting !== null}
              >
                {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF
              </Button>
              <Button
                variant="secondary"
                className="gap-2"
                onClick={() => void handleExportServer("xlsx")}
                disabled={exporting !== null}
              >
                {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
                XLSX
              </Button>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">De</span>
            <DatePicker
              value={from}
              toDate={to || undefined}
              onChange={next => {
                setFrom(next);
                if (next && to && next > to) setTo(next);
              }}
              className="h-8 w-40 bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground text-sm"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs text-primary-foreground/70">Até</span>
            <DatePicker
              value={to}
              fromDate={from || undefined}
              onChange={next => {
                setTo(next);
                if (next && from && next < from) setFrom(next);
              }}
              className="h-8 w-40 bg-white/10 border-white/20 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground text-sm"
            />
          </div>
          {canFilterVendor && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-primary-foreground/70">Vendedor</span>
              <Select value={vendorId} onValueChange={setVendorId}>
                <SelectTrigger className="h-8 w-44 bg-white/10 border-white/20 text-primary-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            variant="secondary"
            className="h-8 gap-1.5"
            onClick={() => void loadReport()}
            disabled={loading}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5" />}
            Atualizar
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      {report && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          {statCards.map(card => (
            <div key={card.label} className="rounded-xl border bg-card p-4 shadow-sm">
              <div className="text-xs text-muted-foreground">{card.label}</div>
              <div className="mt-1 text-xl font-bold tabular-nums">{card.value}</div>
              <div className="text-xs text-muted-foreground">{card.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Per-client table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : report && report.byClient.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-12 text-muted-foreground">
          <TrendingUp className="h-10 w-10 opacity-30" />
          <p className="text-sm">Nenhum pedido encontrado no período</p>
        </div>
      ) : report ? (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="text-sm font-medium text-muted-foreground">
              {vendorLabel && vendorLabel !== "Todos os vendedores" ? `Vendedor: ${vendorLabel}` : "Todos os vendedores"}
              {from && to ? ` · ${from} → ${to}` : ""}
            </span>
            <div className="flex items-center gap-3">
              <Button
                variant={detailed ? "default" : "outline"}
                size="sm"
                className="h-7 gap-1.5"
                onClick={() => setDetailed(v => !v)}
                aria-pressed={detailed}
              >
                <Layers className="h-3.5 w-3.5" />
                Detalhar
              </Button>
              <span className="text-xs text-muted-foreground">{report.byClient.length} clientes</span>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-right">Pedidos</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Entregue</TableHead>
                <TableHead className="text-right">A Entregar</TableHead>
                <TableHead className="text-right">%</TableHead>
                <TableHead className="text-right">Comissão</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.byClient.map((c: ClientSalesSummary) => (
                <Fragment key={c.clientId}>
                  <TableRow
                    onClick={() => void openClient(c)}
                    className={cn(
                      "cursor-pointer",
                      selectedClientId === c.clientId && "bg-primary/5",
                    )}
                  >
                    <TableCell className="font-medium">
                      <span className="text-primary underline-offset-2 hover:underline">{baseClientName(c.clientName)}</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{c.orders}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(c.totalValue)}</TableCell>
                    <TableCell className="text-right tabular-nums text-green-700">{fmt(c.deliveredValue)}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-700">{fmt(c.pendingValue)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">
                      {c.commissionPercent != null ? `${c.commissionPercent}%` : "—"}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {c.commissionAmount != null ? fmt(c.commissionAmount) : "—"}
                    </TableCell>
                  </TableRow>
                  {detailed && (c.byDeliveryUnit ?? []).map((u, idx) => (
                    <TableRow
                      key={`${c.clientId}-${u.deliveryUnitId ?? `none-${idx}`}`}
                      className="bg-muted/20 hover:bg-muted/30"
                    >
                      <TableCell className="py-2 pl-8 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5">
                          <Layers className="h-3 w-3 opacity-60" />
                          {u.deliveryUnitName}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 text-right text-sm tabular-nums text-muted-foreground">{u.orders}</TableCell>
                      <TableCell className="py-2 text-right text-sm tabular-nums text-muted-foreground">{fmt(u.totalValue)}</TableCell>
                      <TableCell className="py-2 text-right text-sm tabular-nums text-green-700/80">{fmt(u.deliveredValue)}</TableCell>
                      <TableCell className="py-2 text-right text-sm tabular-nums text-amber-700/80">{fmt(u.pendingValue)}</TableCell>
                      <TableCell className="py-2 text-right text-sm tabular-nums text-muted-foreground">—</TableCell>
                      <TableCell className="py-2 text-right text-sm tabular-nums text-muted-foreground">—</TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              ))}
              {/* Totals row */}
              <TableRow className="border-t-2 font-semibold bg-muted/30">
                <TableCell>Total</TableCell>
                <TableCell className="text-right tabular-nums">{report.totalOrders}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(report.totalValue)}</TableCell>
                <TableCell className="text-right tabular-nums text-green-700">{fmt(report.deliveredValue)}</TableCell>
                <TableCell className="text-right tabular-nums text-amber-700">{fmt(report.pendingValue)}</TableCell>
                <TableCell className="text-right">—</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(report.commissionTotal ?? 0)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      ) : null}

      {/* Painel do cliente selecionado */}
      {selectedClientId && (
        <div className="rounded-xl border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b px-4 py-3">
            <span className="text-sm font-medium">
              Pedidos de {clientReport?.clientName ? baseClientName(clientReport.clientName) : "cliente"}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 text-muted-foreground"
              onClick={() => { setSelectedClientId(null); setClientReport(null); }}
            >
              <X className="h-3.5 w-3.5" /> Fechar
            </Button>
          </div>

          {clientLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : clientReport ? (
            <div className="grid gap-6 p-4 lg:grid-cols-2">
              {/* Pedidos do cliente */}
              <div>
                <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Pedidos ({clientReport.orders.length})
                </div>
                {clientReport.orders.length === 0 ? (
                  <p className="py-6 text-sm text-muted-foreground">Nenhum pedido no período.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nº</TableHead>
                        <TableHead>Criado</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                        <TableHead>Saída</TableHead>
                        <TableHead className="text-right">Abrir</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clientReport.orders.map(o => (
                        <TableRow key={o.orderId}>
                          <TableCell className="tabular-nums">{o.orderNumber ?? "—"}</TableCell>
                          <TableCell className="tabular-nums text-muted-foreground">{fmtBRDate(o.createdDate)}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmt(o.value)}</TableCell>
                          <TableCell className="tabular-nums text-muted-foreground">{fmtBRDate(o.exitDate)}</TableCell>
                          <TableCell className="text-right">
                            <a
                              href={`/admin/pedidos/${o.orderId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-primary hover:underline"
                            >
                              <ExternalLink className="h-3.5 w-3.5" /> Abrir
                            </a>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              {/* Produtos do cliente */}
              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <Package className="h-3.5 w-3.5" /> Produtos
                  </span>
                  <Select value={productSort} onValueChange={v => setProductSort(v as ProductSort)}>
                    <SelectTrigger className="h-8 w-44 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PRODUCT_SORT_LABELS) as ProductSort[]).map(k => (
                        <SelectItem key={k} value={k}>{PRODUCT_SORT_LABELS[k]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {sortedProducts.length === 0 ? (
                  <p className="py-6 text-sm text-muted-foreground">Sem itens no período.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead className="text-right">Pedidos</TableHead>
                        <TableHead className="text-right">Qtd.</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedProducts.map(p => (
                        <TableRow key={p.productId}>
                          <TableCell className="font-medium">{p.productName}</TableCell>
                          <TableCell className="text-right tabular-nums">{p.orderCount}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmtNumber(p.totalQuantity)} {p.unit}</TableCell>
                          <TableCell className="text-right tabular-nums">{fmt(p.totalValue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
